import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import uniqBy from 'lodash/uniqBy'

import {
  CatalogProductType,
  SortableField,
  SortOrder,
  useMenuSearchQuery,
  VisibilityEnum
} from 'src/apollo/onlineOrdering'
import { NetworkStatus } from '@apollo/client'
import { t } from 'src/translations'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import useOnVisible from 'src/lib/js/hooks/useOnVisible'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useMenuSearchContext } from './MenuSearchContext'
import { sanitizeSearchString } from './searchUtils'
import { ItemSource } from '../online_ordering/cart/cartUtils'
import { RecentSearches } from './RecentSearches'
import { LookupInfoIcon, MenusIcon, AddIcon } from '@toasttab/buffet-pui-icons'
import ItemModal from 'public/components/default_template/online_ordering/item_modal/ItemModal'
import {
  ecommMenuItemPath,
  menuItemPagePath,
  onItemClickImpl
} from 'public/components/default_template/menu_section/MenuItemCard'
import {
  MenuItem,
  DoProduct
} from 'public/components/default_template/menu_section/MenuSection'
import { Channel } from 'public/js/siteUtilities'
import {
  getMenuPath,
  useRestaurantRoutes
} from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { LoadingSpinner } from 'src/shared/components/common/loading_spinner/LoadingSpinner'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'
import { useFormatPrice } from 'src/lib/js/hooks/useFormatPrice'
import { reportError } from 'src/lib/js/clientError'
import { useLocation } from 'react-router'
import { usePageDomains } from 'public/js/PageDomainContext'
import Link from 'shared/components/common/link'
import { useA11y } from '../../online_ordering/useA11y'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { OutOfStockTag } from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import classnames from 'classnames'

export type SearchResultsProps = {
  channelGuid?: string | null
  searchInputRef?: React.RefObject<HTMLInputElement>
  searchModalContentRef?: React.RefObject<HTMLDivElement>
  closeSearchModal?: () => void
}

type SearchResult = {
  guid: string
  name: string
  description: string
  image: string
  price: number
  outOfStock: boolean
  itemGroupGuid: string
  slug?: string
  catalogProductType?: CatalogProductType | null
}

export const DEFAULT_PAGE_SIZE = 15

export const SearchResults = ({
  channelGuid,
  searchInputRef,
  searchModalContentRef,
  closeSearchModal
}: SearchResultsProps) => {
  const { searchString, fromRecentSearches, setFromRecentSearches } =
    useMenuSearchContext()
  const { selectedLocation } = useRestaurant()
  const { track } = useThrottledTracker()
  const { orderPath, ecommPath } = useRestaurantRoutes()
  const { channel, cartGuid } = useCart()
  const { fulfillmentData } = useFulfillment()
  const path = channel === Channel.ECOMM && ecommPath ? ecommPath : orderPath
  const [itemModalOpen, setItemModalOpen] = useState(false)
  const [selectedSearchResult, setSelectedSearchResult] = useState<
    SearchResult | undefined
  >(undefined)
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const numColumns = isMobile ? 2 : 4
  const { announce } = useA11y()
  const ref = useRef<HTMLDivElement>(null)
  const hasMore = useRef(false)
  const { ooSearchPageSize } = useFlags()
  const pageSize = ooSearchPageSize || DEFAULT_PAGE_SIZE

  const shouldSkipQuery = !searchString || searchString.length < 2

  const searchInput = useMemo(
    () => ({
      restaurantGuid: selectedLocation?.externalId || '',
      channelGuid,
      searchTerm: sanitizeSearchString(searchString),
      size: pageSize,
      visibility: channelGuid
        ? undefined
        : [VisibilityEnum.ToastOnlineOrdering],
      consolidateItemsByProduct: channel === Channel.ECOMM,
      sort: [
        { field: SortableField.Score, order: SortOrder.Desc },
        { field: SortableField.VersionId, order: SortOrder.Desc }
      ]
    }),
    [selectedLocation?.externalId, channelGuid, searchString, pageSize, channel]
  )

  const {
    data,
    loading,
    error,
    fetchMore: _fetchMore,
    networkStatus
  } = useMenuSearchQuery({
    variables: { input: searchInput },
    skip: shouldSkipQuery,
    notifyOnNetworkStatusChange: true
  })

  hasMore.current = shouldSkipQuery
    ? false
    : Boolean(data?.searchMenus?.hasMoreResults) &&
      !!data?.searchMenus?.searchAfter

  const showLoadingSpinner = networkStatus === NetworkStatus.fetchMore

  const fetchMore = useCallback(() => {
    if (!hasMore.current || networkStatus === NetworkStatus.fetchMore) {
      return
    }

    const searchAfter = data?.searchMenus?.searchAfter
    // Only paginate if searchAfter is not null/empty — the backend can still omit the cursor
    if (!searchAfter) {
      return
    }

    _fetchMore({
      variables: {
        input: {
          ...searchInput,
          searchAfter
        }
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.searchMenus) {
          return prev
        }

        const prevItems = prev.searchMenus?.menuItems || []
        const newItems = fetchMoreResult.searchMenus.menuItems || []

        return {
          searchMenus: {
            ...fetchMoreResult.searchMenus,
            // Merge new search results with existing results
            menuItems: [...prevItems, ...newItems]
          }
        }
      }
    }).catch((error) => {
      reportError('Error fetching more menu search results', error, {
        searchString,
        searchAfter
      })
    })
  }, [
    _fetchMore,
    searchInput,
    data?.searchMenus?.searchAfter,
    networkStatus,
    searchString
  ])

  useOnVisible(
    ref,
    () => {
      if (loading || !hasMore.current) {
        return
      }

      fetchMore()
    },
    { rootMargin: '1px 1px 1px 1px' }
  )

  const searchResults = useMemo(() => {
    const menuItems = data?.searchMenus?.menuItems || []
    if (!menuItems.length) {
      return []
    }

    // Deduplicate by guid as we could see duplicate menuItemGuids across pages, the transform MenuItemSearchResult to SearchResult format
    return uniqBy(menuItems, 'guid').map(
      (item) =>
        ({
          guid: item.guid,
          name: item.name || '',
          description: item.description || '',
          image: item.image || '',
          price: item.price || 0,
          outOfStock: item.outOfStock || false,
          itemGroupGuid: item.parentMenuGroups?.length
            ? item.parentMenuGroups[item.parentMenuGroups.length - 1]?.guid!
            : '',
          slug: item?.slug,
          catalogProductType: item?.catalogProductType
        } as SearchResult)
    )
  }, [data?.searchMenus?.menuItems])

  useEffect(() => {
    if (!loading && !shouldSkipQuery) {
      track(TrackingEvents.MENU_SEARCH_QUERY_COMPLETE, {
        searchString,
        searchResultsLength: searchResults.length,
        fromRecentSearches
      })
      setFromRecentSearches(false)
      searchInputRef?.current?.focus()
    }
  }, [
    loading,
    searchResults,
    shouldSkipQuery,
    searchString,
    fromRecentSearches,
    track,
    setFromRecentSearches,
    searchInputRef
  ])

  useEffect(() => {
    const searchModalContent = searchModalContentRef?.current
    if (!isMobile) {
      if (selectedSearchResult) {
        searchModalContent?.classList.add('hidden')
      } else {
        searchModalContent?.classList.remove('hidden')
      }
    }
    return () => {
      searchModalContent?.classList.remove('hidden')
    }
  }, [selectedSearchResult, isMobile, searchModalContentRef])

  const onItemSelect = useCallback(
    (item: SearchResult) => {
      setSelectedSearchResult(item)
      onItemClickImpl(
        undefined,
        false,
        item,
        track,
        path,
        () => setItemModalOpen(true),
        cartGuid,
        fulfillmentData?.cartFulfillmentData.deliveryInfo,
        ItemSource.Search
      )
    },
    [cartGuid, fulfillmentData?.cartFulfillmentData.deliveryInfo, path, track]
  )

  useEffect(() => {
    if (loading || shouldSkipQuery) {
      return
    }

    const message =
      searchResults.length > 0
        ? t('sr-only.results-found', { count: searchResults.length })
        : t('sr-only.no-results-found')

    // Defer so this isn't queued behind the same update that re-announces the
    // focused searchbox (programmatic focus or large results DOM changes).
    const timeoutId = window.setTimeout(() => {
      announce(message)
    }, 150)

    return () => window.clearTimeout(timeoutId)
  }, [loading, shouldSkipQuery, searchResults.length, announce])

  const getResultsHeaderText = () => {
    if (loading && networkStatus !== NetworkStatus.fetchMore) {
      return (
        <Skeleton
          width={isMobile ? '40%' : '15%'}
          className='loadingSkeleton'
        />
      )
    }

    // If there are more results to page through, update the header to show an approximate count - "more than (N) results"
    if (hasMore.current) {
      // Show actual count if less than 5, otherwise round down to nearest multiple of 5
      const count =
        searchResults.length < 5
          ? searchResults.length
          : Math.floor(searchResults.length / 5) * 5
      return t('menu-banner.search-results-header-more-than', {
        count
      })
    }

    // Otherwise show actual count
    return t('menu-banner.search-results-header', {
      count: searchResults.length
    })
  }

  if (error) {
    return (
      <div className='search-results-container'>
        <div className='search-placeholder'>
          <p>{t('menu-banner.search-errors')}</p>
        </div>
      </div>
    )
  }

  if (!searchString) {
    return (
      <div className='search-results-container'>
        <div className='search-placeholder'>
          <RecentSearches />
        </div>
      </div>
    )
  }

  if (!loading && searchResults.length === 0) {
    return (
      <div className='search-results-container'>
        <div className='search-placeholder'>
          <LookupInfoIcon />
          <p>{t('menu-banner.search-no-results-found')}</p>
          <p className='search-subheader'>
            {t('menu-banner.search-no-results-found.subheader')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='search-results-container'>
      <h3 className='search-results-header'>{getResultsHeaderText()}</h3>
      <div className='searchResults'>
        {loading
          ? Array.from({ length: numColumns }).map((_, index: number) => (
              <LoadingSearchResultSkeleton key={index} />
            ))
          : null}
        {searchResults?.map((item) => (
          <SearchResultItem
            key={item.guid}
            item={item}
            onSelect={() => onItemSelect(item)}
            isEcommItem={channel === Channel.ECOMM}
          />
        ))}
        <ItemModal
          isOpen={itemModalOpen}
          onClose={() => {
            setItemModalOpen(false)
            setSelectedSearchResult(undefined)
            window.history.replaceState(null, '', path)
          }}
          itemGuid={selectedSearchResult?.guid}
          itemGroupGuid={selectedSearchResult?.itemGroupGuid}
          itemSource={ItemSource.Search}
          onSuccessfulAddToCart={closeSearchModal}
        />
      </div>
      {hasMore.current && (
        <div ref={ref} className='loader' data-testid='loader'>
          {showLoadingSpinner && !!searchResults.length && <LoadingSpinner />}
        </div>
      )}
    </div>
  )
}

const LoadingSearchResultSkeleton = () => {
  return (
    <div className='searchResultItem' data-testid='loadingSearchResultSkeleton'>
      <Skeleton className='image' />
      <Skeleton className='title' style={{ width: '80%' }} />
      <Skeleton className='price' style={{ width: '40%' }} />
    </div>
  )
}

const SearchResultItem = ({
  item,
  onSelect,
  isEcommItem
}: {
  item: SearchResult
  onSelect: () => void
  isEcommItem: boolean
}) => {
  const { selectedLocation } = useRestaurant()
  const { ecommPath } = useRestaurantRoutes()
  const location = useLocation()
  const { host, pageDomainOverrides } = usePageDomains()

  const showItemPDP =
    isEcommItem ||
    location.pathname.startsWith('/menu') ||
    pageDomainOverrides.get('/menu') === host

  const product = useMemo(() => {
    // Only construct DoProduct for OPTION products, which are the ones served by
    // the /products/v/{slug} routes SitesRoutes registers
    return item.catalogProductType === CatalogProductType.Option && item.slug
      ? ({
          slug: item.slug,
          type: item.catalogProductType,
          guid: item.guid,
          name: item.name,
          description: item.description,
          imageLink: item.image,
          outOfStock: item.outOfStock,
          itemGroupGuid: item.itemGroupGuid,
          prices: item.price ? [item.price] : [],
          // variants is intentionally empty — ProductDisplayPage re-fetches the data. State is used when loading
          variants: [] as MenuItem[],
          availableOptions: [],
          itemTags: []
        } as DoProduct)
      : undefined
  }, [item])

  const pdpHref = useMemo(() => {
    if (!showItemPDP) return undefined
    return isEcommItem
      ? ecommMenuItemPath(ecommPath || '', item, product)
      : menuItemPagePath(
          getMenuPath(selectedLocation.shortUrl),
          item.itemGroupGuid,
          item.name || '',
          item.guid
        )
  }, [
    ecommPath,
    isEcommItem,
    item,
    product,
    selectedLocation.shortUrl,
    showItemPDP
  ])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
        onSelect()
      }
    },
    [onSelect]
  )

  const Wrapper = useCallback(
    ({
      outOfStock,
      children
    }: React.PropsWithChildren<{ outOfStock: boolean }>) =>
      showItemPDP && pdpHref ? (
        <Link
          href={pdpHref}
          state={
            product ? { product } : { menuItem: item, pathName: ecommPath }
          }
          testId={`search-result-item-${item.guid}`}
          className='searchResultItem'
          ariaLabel={item.name || t('search-modal.search-results.open-item')}
        >
          {children}
        </Link>
      ) : (
        <div
          role='button'
          className={classnames(
            'searchResultItem',
            outOfStock ? 'outOfStock' : ''
          )}
          data-testid={`search-result-item-${item.guid}`}
          aria-label={item.name || t('search-modal.search-results.open-item')}
          onClick={outOfStock ? undefined : onSelect}
          onKeyDown={outOfStock ? undefined : handleKeyDown}
          tabIndex={outOfStock ? -1 : 0}
          aria-haspopup={outOfStock ? undefined : 'dialog'}
          aria-disabled={outOfStock || undefined}
        >
          {children}
        </div>
      ),
    [showItemPDP, pdpHref, item, product, ecommPath, onSelect, handleKeyDown]
  )

  const formattedPrice = useFormatPrice(item.price ?? 0, true)

  return (
    <Wrapper outOfStock={item.outOfStock}>
      <div className='image'>
        {item.image ? (
          <img
            className='imgWrapper'
            src={item.image}
            alt=''
            data-testid={item.name + '-image'}
          />
        ) : (
          !isEcommItem && <MenusIcon accessibility='decorative' />
        )}
        {item.outOfStock ? (
          <OutOfStockTag />
        ) : (
          <div className='quickAdd'>
            <AddIcon accessibility='decorative' />
          </div>
        )}
      </div>
      <p className='title'>{item.name}</p>
      <p className='price'>{formattedPrice}</p>
    </Wrapper>
  )
}
